/**
 * StagesFilter - Filtro de etapas do funil (formato select com ícone).
 * Refatorado: Usa hook utilitário useClickOutside.
 */
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from '../../icons/AppIcon';
import { FunnelStage } from '../../../types';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { cn } from '@/lib/utils';

interface StagesFilterProps {
    stages: FunnelStage[];
    selectedStage: string;
    onSelect: (stageId: string) => void;
    triggerClassName?: string;
    /** Sem borda própria — para faixa unificada no mobile */
    embedded?: boolean;
}

export const StagesFilter: React.FC<StagesFilterProps> = ({
    stages,
    selectedStage,
    onSelect,
    triggerClassName,
    embedded = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    // Hook utilitário
    useClickOutside(dropdownRef, () => setIsOpen(false), isOpen, [buttonRef]);

    const hasSelection = selectedStage !== 'all' && selectedStage !== '';

    const calculatePosition = () => {
        if (!buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 180;
        const viewportWidth = window.innerWidth;

        let left = rect.left;
        if (rect.left + dropdownWidth > viewportWidth - 16) {
            left = rect.right - dropdownWidth;
        }

        setDropdownStyle({
            position: 'fixed',
            top: `${rect.bottom + 4}px`,
            left: `${left}px`,
            zIndex: 9999
        });
    };

    const toggleDropdown = () => {
        if (!isOpen) {
            calculatePosition();
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (stageId: string) => {
        onSelect(stageId);
        setIsOpen(false);
    };

    const selectedStageName = selectedStage === 'all' || selectedStage === ''
        ? 'Todas'
        : stages.find(s => s.id === selectedStage)?.name || 'Todas';

    return (
        <>
            <button
                ref={buttonRef}
                onClick={toggleDropdown}
                className={cn(
                    'relative group flex shrink-0 items-center justify-center transition-colors',
                    embedded
                        ? 'h-11 w-full min-w-0 rounded-none border-0'
                        : 'h-9 w-9 rounded-lg border bg-card',
                    hasSelection
                        ? embedded
                            ? 'bg-primary-subtle text-primary'
                            : 'border-primary-light bg-primary-subtle text-primary'
                        : embedded
                            ? 'bg-transparent text-secondary hover:bg-surface-subtle hover:text-primary'
                            : 'border-border text-secondary hover:bg-surface-subtle hover:text-primary',
                    triggerClassName,
                )}
                title={`Filtrar por etapa: ${selectedStageName}`}
                aria-label="Filtrar por etapas"
            >
                <AppIcon
                    name="pipeline"
                    className="h-5 w-5 transition-colors"
                />
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    style={dropdownStyle}
                    className="w-[180px] bg-background rounded-lg shadow-xl border border-border py-1 z-dropdown"
                >
                    {/* Opção "Todas" */}
                    <button
                        onClick={() => handleSelect('all')}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-surface-subtle transition-colors flex items-center justify-between ${selectedStage === 'all' || selectedStage === ''
                            ? 'font-semibold text-primary bg-primary/10'
                            : 'text-secondary'
                            }`}
                    >
                        <span>Todas as etapas</span>
                        {(selectedStage === 'all' || selectedStage === '') && (
                            <AppIcon name="check" className="w-4 h-4" />
                        )}
                    </button>

                    <div className="border-t border-border my-1" />

                    {/* Lista de etapas */}
                    <div className="max-h-[200px] overflow-y-auto">
                        {stages.map(stage => (
                            <button
                                key={stage.id}
                                onClick={() => handleSelect(stage.id)}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-surface-subtle transition-colors flex items-center justify-between ${selectedStage === stage.id
                                    ? 'font-semibold text-primary bg-primary/10'
                                    : 'text-secondary'
                                    }`}
                            >
                                <span>{stage.name}</span>
                                {selectedStage === stage.id && (
                                    <AppIcon name="check" className="w-4 h-4" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default StagesFilter;

